import React, { useEffect, useState } from 'react';
import { arrayOf, number, string } from 'prop-types';
import { useRouter } from 'next/router';
import { StringParam, useQueryParam } from 'next-query-params';
import { useAuth0 } from '@auth0/auth0-react';

import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, Tooltip, Typography } from '@material-ui/core';

import RefreshIcon from '@material-ui/icons/Refresh';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import { PATHS } from '../../../modules/Flow';
import { useSaveQuote } from '../../../hooks/api-v2';
import { apiClient } from '../../../../apollo';
import SaveQuoteConfirmation from './SaveQuoteConfirmation';

const useStyles = makeStyles(theme => ({
  container: {
    width: '100vw',
    height: 50,
    backgroundColor: theme.palette.neutral.lighter,
    padding: theme.spacing(0, 8),
    borderBottom: `1px solid ${theme.palette.neutral.light}`,
  },
  text: {
    color: theme.palette.neutral.medium,
  },
  buttonIcon: {
    color: theme.palette.neutral.medium,
    height: 15,
    width: 15,
  },
  button: {
    height: 32,
    width: 120,
    borderRadius: 24,
  },
  tooltip: {
    fontSize: 18,
  },
}));

const ProgressManager = ({ currentPageIndex, flow }) => {
  const classes = useStyles();

  const [opportunityId] = useQueryParam('opportunityId', StringParam);
  const router = useRouter();
  const { getIdTokenClaims } = useAuth0();

  const [quoteSaved, saveQuoteUrl] = useSaveQuote();

  const [savedByUserEmail, setSavedByUserEmail] = useState('');
  const [quoteSavedConfirmation, setQuoteSavedConfirmation] = useState(false);

  useEffect(async () => {
    const claims = await getIdTokenClaims();
    setSavedByUserEmail(claims.email);
  }, []);

  const handleSaveQuote = () => {
    if (!opportunityId) return;
    saveQuoteUrl({
      quoteUrl: `${window.location.origin}${router.basePath}${router.asPath}`,
      opportunityId,
      savedByUserEmail,
    });
  };

  useEffect(() => {
    if (quoteSaved) {
      setQuoteSavedConfirmation(true);
      setTimeout(() => setQuoteSavedConfirmation(false), 15000); // After 15 seconds, close the confirm automatically
    }
  }, [quoteSaved]);

  const allowSaveQuote = currentPageIndex && currentPageIndex + 1 !== flow.length; // Alow save quote on every page except first and last

  return (
    <Box position="relative" display="flex" justifyContent="center">
      <Box className={classes.container} display="flex" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          className={classes.button}
          onClick={() =>
            router.push({
              pathname: PATHS.SERVICE,
              search: '',
            })
          }
        >
          <Box display="flex" alignItems="center" justifyContent="space-evenly" width="100%">
            <RefreshIcon className={classes.buttonIcon} />
            <Typography variant="body2" className={classes.text}>
              RESET
            </Typography>
          </Box>
        </Button>
        <Typography variant="body2" className={classes.text}>{`STEP ${currentPageIndex + 1}/${flow.length}`}</Typography>
        {allowSaveQuote ? (
          <Tooltip
            title="This quote cannot be linked to an opportunity in Salesforce. Please complete this order without saving a quote or restart the order through an opportunity in Salesforce in order to save a quote."
            disableFocusListener={Boolean(opportunityId)}
            disableHoverListener={Boolean(opportunityId)}
            classes={{ tooltip: classes.tooltip }}
          >
            <Button variant="outlined" className={classes.button} onClick={handleSaveQuote}>
              <Box display="flex" alignItems="center" justifyContent="space-evenly" width="100%">
                <Typography variant="body2" className={classes.text}>
                  SAVE
                </Typography>
                <SaveOutlinedIcon className={classes.buttonIcon} />
              </Box>
            </Button>
          </Tooltip>
        ) : (
          <Box width={120} />
        )}
      </Box>
      {quoteSavedConfirmation && <SaveQuoteConfirmation closeConfirmation={() => setQuoteSavedConfirmation(false)} />}
    </Box>
  );
};

ProgressManager.propTypes = {
  currentPageIndex: number.isRequired,
  flow: arrayOf(string.isRequired).isRequired,
};

export default apiClient(ProgressManager);
